import React from "react";
import { ShieldCheck, Award, Snowflake, Cpu, Star } from "lucide-react";
import BookingWizard from "@/components/site/BookingWizard";

const FEATURES = [
  { icon: ShieldCheck, title: "ASE Certified Technicians", sub: "Our team holds industry-leading certifications" },
  { icon: Award, title: "Manufacturer Warranty", sub: "All parts backed by manufacturer warranty" },
  { icon: Snowflake, title: "Ottawa Winter-Ready Care", sub: "Specialized seasonal inspection and maintenance packages" },
  { icon: Cpu, title: "Latest Equipment", sub: "State-of-the-art diagnostic and repair tools" },
];

export default function Hero({ symptomTopic, api }) {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/20190807/pexels-photo-20190807.png?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Luxury sports car in dark garage"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/85 to-[#121212]/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(245,197,24,0.08),_transparent_50%)]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        {/* Left col */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#F5C518]/30 bg-[#F5C518]/5 mb-6 fade-in-up">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#F5C518] opacity-75 animate-ping" />
              <span className="relative inline-flex w-2 h-2 rounded-full bg-[#F5C518]" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#F5C518]">
              Ottawa · Open & Booking Today
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter fade-in-up stagger-1"
          >
            Precision.
            <br />
            Performance.
            <br />
            <span className="text-[#F5C518]">Perfection.</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-neutral-400 max-w-xl leading-relaxed fade-in-up stagger-2">
            Ottawa&apos;s elite full-service mechanic shop. Dealership-grade diagnostics,
            ASE-certified technicians, and winter-ready care built for Canadian roads — without the dealership markup.
          </p>

          {/* 4-column feature grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 fade-in-up stagger-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  data-testid={`feature-${i}`}
                  className="group p-4 rounded-lg border border-white/8 bg-white/[0.02] hover:border-[#F5C518]/40 hover:bg-white/[0.04] transition-all duration-300"
                >
                  <Icon className="w-5 h-5 text-[#F5C518] mb-3" strokeWidth={2} />
                  <h3 className="font-display font-bold text-sm uppercase tracking-wide text-white leading-snug">
                    {f.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-neutral-400 leading-relaxed">{f.sub}</p>
                </div>
              );
            })}
          </div>

          {/* 5-star social proof */}
          <div
            data-testid="social-proof"
            className="mt-10 flex items-center gap-3 fade-in-up stagger-4"
          >
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-[#F5C518] text-[#F5C518]"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <span className="text-sm font-medium text-neutral-300 uppercase tracking-wider">
              Rated 5 Stars by Ottawa Drivers
            </span>
          </div>
        </div>

        {/* Right col - Callback form */}
        <div className="lg:col-span-5 fade-in-up stagger-2" id="booking">
          <BookingWizard api={api} initialNotes={symptomTopic} />
        </div>
      </div>
    </section>
  );
}
